import { apiService } from './apiService';
import { Meal } from '../models/diary';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';

export interface MealsPaginatedResponse {
  count: number;
  results: Meal[];
  totalPages: number;
  actualPage: number;
}

export interface MealIngredientInput {
  ingredient_id: number;
  quantity: number; // grams
}

export interface MealPayload {
  name: string;
  notes?: string;
  // When provided, replaces the whole ingredient list and recomputes calories.
  // Omit to leave ingredients untouched (PATCH only).
  ingredients?: MealIngredientInput[];
}

// Normalize the API `items` array onto the Meal.ingredients shape used across the app.
const normalizeMeal = (raw: any): Meal => ({
  id: raw.id,
  name: raw.name,
  notes: raw.notes || '',
  calories: raw.calories,
  ingredients: (raw.items || raw.ingredients || []).map((it: any) => ({
    ingredient_id: it.ingredient_id,
    name: it.name,
    quantity: it.quantity,
    calories: it.calories,
    carbohydrates: it.carbohydrates,
    proteins: it.proteins,
    fats: it.fats,
    water: it.water,
    fiber: it.fiber,
  })),
});

export const mealService = {
  getAll: async (
    patientId: string | number,
    page: number = 1,
    perpage: number = 100,
    search: string = ''
  ): Promise<MealsPaginatedResponse> => {
    const params = new URLSearchParams({ page: String(page), perpage: String(perpage) });
    if (search.trim()) params.set('search', search.trim());
    const url = `${API_BASE_URL}/clinic/patient/${patientId}/meals/?${params.toString()}`;
    try {
      const response = await apiService.get(url);
      return {
        count: response.count,
        results: (response.results || []).map(normalizeMeal),
        actualPage: page,
        totalPages: Math.ceil(response.count / perpage) || 1,
      };
    } catch (error) {
      console.error(`Error fetching meals for patient ${patientId}:`, error);
      throw error;
    }
  },

  getOne: async (patientId: string | number, mealId: number): Promise<Meal> => {
    const url = `${API_BASE_URL}/clinic/patient/${patientId}/meals/${mealId}/`;
    try {
      const response = await apiService.get(url);
      return normalizeMeal(response);
    } catch (error) {
      console.error(`Error fetching meal ${mealId}:`, error);
      throw error;
    }
  },

  create: async (patientId: string | number, data: MealPayload): Promise<Meal> => {
    const url = `${API_BASE_URL}/clinic/patient/${patientId}/meals/`;
    try {
      const response = await apiService.post(url, data);
      return normalizeMeal(response);
    } catch (error) {
      console.error(`Error creating meal for patient ${patientId}:`, error);
      throw error;
    }
  },

  update: async (patientId: string | number, mealId: number, data: Partial<MealPayload>): Promise<Meal> => {
    const url = `${API_BASE_URL}/clinic/patient/${patientId}/meals/${mealId}/`;
    try {
      const response = await apiService.patch(url, data);
      return normalizeMeal(response);
    } catch (error) {
      console.error(`Error updating meal ${mealId}:`, error);
      throw error;
    }
  },

  // 204 normally; 409 if still attached to a diary item (must detach first).
  remove: async (patientId: string | number, mealId: number): Promise<void> => {
    const url = `${API_BASE_URL}/clinic/patient/${patientId}/meals/${mealId}/`;
    try {
      await apiService.delete(url);
    } catch (error) {
      console.error(`Error deleting meal ${mealId}:`, error);
      throw error;
    }
  },
};
